import { Star, ChevronRight } from "lucide-react";
import { useTheme } from "@/components/ThemeContext";
import ScrollReveal from "@/components/ScrollReveal";

const GOOGLE_REVIEWS_URL = "https://www.google.com/maps/place/Transfer+Avellino/@40.9146076,14.7715835,3397m/data=!3m1!1e3!4m18!1m9!3m8!1s0x133bcd0078462257:0x919a312e8ee3ad67!2sTransfer+Avellino!8m2!3d40.9146076!4d14.7715835!9m1!1b1!16s%2Fg%2F11ygwy0b9y!3m7!1s0x133bcd0078462257:0x919a312e8ee3ad67!8m2!3d40.9146076!4d14.7715835!9m1!1b1!16s%2Fg%2F11ygwy0b9y?entry=ttu";

function GoogleIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function ReviewCard({ review }) {
  const { isDark } = useTheme();

  return (
    <div className={`border p-6 md:p-8 hover:border-[#C9A646]/30 transition-all duration-500 h-full flex flex-col ${isDark ? "bg-[#141414] border-white/5" : "bg-white border-black/5 shadow-sm"}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4285F4] to-[#34A853] flex items-center justify-center text-white font-medium text-sm">
            {review.avatar_letter || review.nome.charAt(0)}
          </div>
          <div>
            <p className={`text-sm font-medium ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>{review.nome}</p>
            <p className={`text-[10px] ${isDark ? "text-[#A1A1AA]" : "text-[#888]"}`}>
              {review.data_recensione || "Recensione Google"}
            </p>
          </div>
        </div>
        <GoogleIcon className="w-5 h-5 flex-shrink-0 opacity-60" />
      </div>
      <div className="flex gap-0.5 mb-3">
        {Array.from({ length: review.rating }).map((_, si) => (
          <Star key={`star-${si}`} className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
        ))}
      </div>
      <p className={`text-sm leading-relaxed flex-1 ${isDark ? "text-[#A1A1AA]" : "text-[#555]"}`}>
        &ldquo;{review.testo}&rdquo;
      </p>
    </div>
  );
}

export default function ReviewsSection({ testimonials }) {
  const { isDark } = useTheme();

  return (
    <section data-testid="testimonials-section" className={`py-20 md:py-32 transition-colors duration-500 ${isDark ? "bg-[#0A0A0A]" : "bg-[#F5F0E8]"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <ScrollReveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <span className="text-xs font-medium tracking-[0.2em] uppercase text-[#C9A646] block mb-4">Recensioni Google</span>
              <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-normal ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>
                Cosa dicono i nostri<span className="italic text-[#C9A646]"> clienti</span>
              </h2>
            </div>
            <div className={`flex items-center gap-4 p-5 border ${isDark ? "bg-[#141414] border-white/5" : "bg-white border-black/5 shadow-sm"}`}>
              <GoogleIcon className="w-8 h-8 flex-shrink-0" />
              <div>
                <div className="flex items-center gap-2">
                  <span className={`text-2xl font-bold ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>5.0</span>
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={`badge-star-${i}`} className="w-4 h-4 fill-[#FBBC05] text-[#FBBC05]" />
                    ))}
                  </div>
                </div>
                <span className={`text-xs ${isDark ? "text-[#A1A1AA]" : "text-[#888]"}`}>{testimonials.length} recensioni su Google</span>
              </div>
            </div>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testimonials.slice(0, 6).map((t) => (
            <ScrollReveal key={t.id} delay={0.08}>
              <ReviewCard review={t} />
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal delay={0.3}>
          <div className="mt-10 text-center">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center gap-3 border border-[#C9A646]/40 px-8 py-4 hover:border-[#C9A646] hover:bg-[#C9A646]/5 transition-all duration-300 group"
            >
              <GoogleIcon className="w-5 h-5" />
              <span className={`text-sm tracking-wider uppercase font-medium ${isDark ? "text-white" : "text-[#1A1A1A]"}`}>
                Vedi tutte le recensioni su Google
              </span>
              <ChevronRight className="w-4 h-4 text-[#C9A646] group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
